import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RadioChangeEvent } from 'antd';
import { useEditUnit } from '../useEditUnit';
import { useQueryDictionary } from '@hooks/useQueryDictionary';
import { useFeedbackForm } from './feedback-form-editor/useFeddbackFormEdit';
import {
  useGetUrlLazyQuery,
  UpdateVideoContentInput,
  HeaPresentationModuleFragment,
  Video
} from '@api/graphql-codegen';
import { handleUploadVideo } from '@services/Video';
import { useHeaGroupModuleCRUD } from './useHeaGroupModuleCRUD';
import { useHeaModuleCRUD } from './useHeaModuleCRUD';
import { useSchoolGroupsModuleCRUD } from './useSchoolGroupsModuleCRUD';
import { useSchoolModuleCRUD } from './useSchoolModuleCRUD';
import { sleep } from '@utils/helpers';
import { Content, ContentType, DefaultModuleEnum, ExtendedContent, UnitTypes } from '@types';
import { logger } from '../../../../../utils/logger';
import { useToast } from '@src/hooks';


export const emptyErrors = {
  name: '',
  position: '',
  feedbackForm: '',
};

export type ErrorKeys = keyof typeof emptyErrors;

export const useEditContent = () => {
  const { content, lesson, heaUnit, isPreparation } = useEditUnit();
  const [customLoading, setLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [currentUnitType, setUnitType] = useState<UnitTypes>(UnitTypes.School);

  const [fileUploadingPercent, setFileUploadingPercent] = useState(0);
  const [fileUploadFinished, setFileUploadFinished] = useState<boolean>(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [data, setData] = useState<string | null>(null);
  const [formData, setFormData] = useState<string>('');
  const [groups, setGroups] = useState<Array<ExtendedContent>>([]);
  const [heaGroups, setHeaGroups] = useState<Array<ExtendedContent>>([]);
  const [schoolGroups, setSchoolGroups] = useState<Array<ExtendedContent>>([]);
  const [pauseHere, setPauseHere] = useState<boolean>(false);
  const [isRequired, setIsRequired] = useState<boolean>(false);
  const [isLastLesson, setIsLastLesson] = useState<boolean>(false);
  const [isAdditionalAfter, setIsAdditionalAfter] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(groups[0]?.['groupName'] || DefaultModuleEnum.Default);
  const dataInputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<Partial<Record<ErrorKeys, string>>>(emptyErrors);
  const [homeEdState, setHomeEdState] = useState<DefaultModuleEnum>(DefaultModuleEnum.Default);
  const { getFormState, hasError: hasFeedbackFormErrors } = useFeedbackForm();
  const { errorToast } = useToast();

  const [getUrl, { loading: urlLoading }] = useGetUrlLazyQuery({
    fetchPolicy: 'network-only',
  });
  const isParentPreparationModule = useMemo(() => !!isPreparation && content?.__typename === 'HEAModule', [content?.__typename, isPreparation]);
  const isHeaModuleTab = useMemo(() => currentUnitType === UnitTypes.HomeEd || isParentPreparationModule, [currentUnitType, isParentPreparationModule]);
  const isTeacherPreparationModule = useMemo(() => !!isPreparation && content?.__typename === 'Content', [content?.__typename, isPreparation]);
  const defaultGroupModule: ExtendedContent | undefined = useMemo(() => (content?.id ? {...content, contentId: content?.id, groupName: DefaultModuleEnum.Default, groupId: ''} : undefined), [content])
  const heaLesson = useMemo(() => (heaUnit?.classPresentation || []).find(cp => cp.originalLessonId === lesson?.id), [heaUnit?.classPresentation, lesson?.id]);
  const homeEdModule: HeaPresentationModuleFragment | Content | undefined = useMemo(() => {
    if (!content?.id) {
      return;
    }
    if (isPreparation) {
      const heaModule = (heaUnit?.parentPreparation || []).find(hm => hm.id === content?.id);
      if (heaModule) {
        return { id: heaModule.id, name: heaModule.name, type: heaModule.type, data: heaModule.data, formData: heaModule.formData, position: heaModule.position, meta: heaModule.meta } as Content
      }
    }
    const heaLesson = (heaUnit?.classPresentation || []).find(cp => cp.originalLessonId === lesson?.id);
    if (!heaLesson) {
      return {id: content.id, type: content.type, name: '', contentId: content?.id, groupName: DefaultModuleEnum.HomeEd, groupId: DefaultModuleEnum.HomeEd};
    }
    const heaModule = heaLesson.modules?.find(hm => hm.id === content?.id);
    if (heaModule) {
      return heaModule;
    }
    return {id: content.id, type: content.type, name: '', contentId: content?.id, groupName: DefaultModuleEnum.HomeEd, groupId: DefaultModuleEnum.HomeEd};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.id, heaUnit?.classPresentation, heaUnit?.parentPreparation, isPreparation, lesson?.id]);

  const homeEdGroupModule: ExtendedContent | undefined = useMemo(() => {
    if (homeEdModule) {
      return {...homeEdModule, contentId: homeEdModule.id, groupId: `HomeEd_${homeEdModule.id}`, groupName: DefaultModuleEnum.HomeEd };
    }
    return undefined;
  }, [homeEdModule]);

  // Whether this module's Home Ed version exists on the server yet. useDefault reads from
  // lesson_module_presentation.original, which heaUpdateModule flips in the same
  // transaction that inserts the hea_module row, so `false` means the row is really there
  // rather than the radio merely having been switched in local state.
  //
  // That value comes from the heaUnit context, which useHeaModuleCRUD does not refresh
  // after a save (it updates the Apollo cache only - setHeaUnit is commented out there),
  // so it stays stale until the page reloads. savedHeaModuleIds covers the gap for saves
  // made in this session.
  const [savedHeaModuleIds, setSavedHeaModuleIds] = useState<string[]>([]);
  const isHomeEdModuleSaved = useMemo(
    () => (homeEdModule as { useDefault?: boolean } | undefined)?.useDefault === false
      || (!!content?.id && savedHeaModuleIds.includes(content.id)),
    [homeEdModule, content?.id, savedHeaModuleIds]
  );

  const markHeaModuleSaved = useCallback((moduleId?: string | null) => {
    if (!moduleId) return;
    setSavedHeaModuleIds(ids => ids.includes(moduleId) ? ids : [...ids, moduleId]);
  }, []);

  const defaultSchoolContentGroups: ExtendedContent[] = useMemo(() =>
      defaultGroupModule ? [defaultGroupModule] : []
    , [defaultGroupModule]);
  const defaultHomeEdContentGroups = useMemo(() => homeEdGroupModule ? [homeEdGroupModule] : [], [homeEdGroupModule]);

  const groupOptions = useQueryDictionary({ dictionary: 'ProgramTypes', isSkip: isHeaModuleTab });

  const availableGroupOptions = useMemo(() => {
    const existingGroups = groups.map(g => g.groupId || (isHeaModuleTab ? DefaultModuleEnum.HomeEd : DefaultModuleEnum.Default));
    const options = isHeaModuleTab ? [{value: '4', label: 'Faith Program'}] : groupOptions;
    return options.filter(g => !existingGroups.includes(g.value));
  }, [groupOptions, groups, isHeaModuleTab]);
  const currentContent = useMemo(() => groups.find(g => g.groupName === activeTab), [activeTab, groups]);
  const isGroupContent = useMemo(() => activeTab !== DefaultModuleEnum.Default && activeTab !== DefaultModuleEnum.HomeEd, [activeTab]);
  /* ------------------------------------------------------------------------------------------------------------------------------------------------------------ */

  const onChangeData = useCallback((value: string | null) => {
    setData(value);
  }, []);

  const onChangeGroupTabs = useCallback((value: ExtendedContent[] | ((v: ExtendedContent[]) => ExtendedContent[])) => {
    setGroups(value);
  }, []);

  const clearFile = useCallback(() => {
    if (dataInputRef.current) {
      dataInputRef.current.value = '';
    }
    setFileUploadingPercent(0);
    setFileUploadFinished(false);
  }, []);

  const updateLocalContent = useCallback((groupContent?: ExtendedContent | Content, currentActiveTab?: string) => {
    setDescription(groupContent?.description ?? '');
    setFileUploadingPercent(0);
    setFileUploadFinished(false);
    setPauseHere(groupContent?.pauseHere ?? false);
    if (groupContent?.type === ContentType.FORM) {
      setFormData(groupContent?.formData ?? '');
      setData('');
    } else {
      setData(groupContent?.data ?? '');
    }
  }, []);

  const onChangeTab = useCallback((key: string) => {
    clearFile();
    setActiveTab(key);
    if (groups?.length > 0) {
      const currentContentByGroup = groups.find(g => g.groupName === key);
      if (currentContentByGroup) {
        updateLocalContent(currentContentByGroup, key);
      }
    }
  }, [clearFile, groups, updateLocalContent]);

  const {
    updateContent,
    updateVideoContent,
    deleteModuleData,
    removeContent,
    isRemovingModule,
    deletingModuleData,
    loading: schoolModuleLoading
  } = useSchoolModuleCRUD({ currentTabContent: currentContent, groups, clearFile, activeTab, isGroupContent, onChangeData, onChangeGroupTabs });

  const {
    getContentsByGroup,
    createNewGroupContentMutation,
    updateGroupContent,
    deleteGroupModuleData,
    deleteGroupContent,
    updateVideoGroupContent,
    isGroupRemoving: isSchoolGroupRemoving,
    isGroupsLoading,
    handleRemoveGroup,
    isOpenAddModal,
    handleAddGroup,
    onCloseAddGroupModal,
  } = useSchoolGroupsModuleCRUD({ currentTabContent: currentContent, groups, clearFile, activeTab, isGroupContent, onChangeData, onChangeGroupTabs, onChangeTab });

  const {
    updateHeaModule,
    deleteHeaModule,
    isHeaModuleRemoving,
    loading: heaModuleLoading,
  } = useHeaModuleCRUD({ heaGroups: groups, currentTabContent: currentContent, clearFile, activeTab, onChangeData, onChangeGroupTabs });

  const {
    getAllHeaGroupModules,
    createHeaGroupModule,
    updateHeaGroupModule,
    deleteHeaGroupModule,
    loading: heaGroupLoading,
    isGroupsLoading: isHeaGroupsLoading,
    isGroupRemoving: isHomeEdGroupRemoving,
    handleRemoveGroup: handleHomeEdRemoveGroup,
  } = useHeaGroupModuleCRUD({ heaGroups: groups, onChangeGroupTabs, currentTabContent: currentContent, activeTab, onChangeTab, clearFile });

  /* ------------------------------------------------------------------------------------------------------------------------------------------------------------ */

  const onAddGroup = useCallback(async (groupName: string, groupId: string) => {
    onCloseAddGroupModal();
    if (currentContent?.id) {
      setData('');
      setFormData('');
      const newGroupContent = { id: currentContent.id, contentId: currentContent.id, type: currentContent.type, name: currentContent.name, parent: currentContent.parent, groupName, groupId } as ExtendedContent;
      if (currentContent.type === ContentType.FORM) {
        newGroupContent.formData = currentContent.formData;
      }

      if (isHeaModuleTab) {
        const result = await createHeaGroupModule({ variables: { input: { moduleId: currentContent.id, groupId: groupId, groupName }} });
        if (result?.data?.heaCreateGroupModule?.groupName) {
          if (newGroupContent.formData) {
            setFormData(newGroupContent.formData);
          }
          setGroups(tabs => {
            const existing = tabs.find(t => t.groupName === groupName);
            if (existing) {
              return tabs;
            } else {
              return ([...tabs, newGroupContent])
            }
          });
          onChangeTab(groupName);
        }
      } else {
        setGroups(groups => ([...groups, newGroupContent]));
        const result = await createNewGroupContentMutation({ variables: { input: { id: currentContent.id, group: groupId, groupName }} });
        if (result?.data?.createNewGroupContent?.groupName) {
          // setGroups(tabs => ([...tabs, newGroupContent]));
          if (newGroupContent.formData) {
            setFormData(newGroupContent.formData);
          }
          onChangeTab(groupName);
        }
      }
    }
  }, [onCloseAddGroupModal, currentContent, isHeaModuleTab, createHeaGroupModule, onChangeTab, createNewGroupContentMutation]);

  const handleUnitTypeChange = useCallback((currentContentId?: string) => (e: RadioChangeEvent) => {
    if (isGroupsLoading) {
      return;
    }

    if (e.target.value && e.target.value !== currentUnitType) {
      setUnitType(e.target.value);
      if (currentContentId === content?.id) {
        if (e.target.value === UnitTypes.HomeEd) {
          setSchoolGroups([...groups]);
          setGroups(heaGroups?.length ? [...heaGroups] : [...defaultHomeEdContentGroups]);
          setActiveTab(DefaultModuleEnum.HomeEd);
          const currentModule = heaGroups[0];
          if (currentModule?.type === ContentType.FORM) {
            setFormData(currentModule?.formData ?? '');
          } else {
            setData(currentModule?.data ?? '');
          }
        } else {
          setHeaGroups([...groups]);
          setGroups(schoolGroups?.length ? [...schoolGroups] : [...defaultSchoolContentGroups]);
          setActiveTab(DefaultModuleEnum.Default);
          const currentModule = schoolGroups[0];
          if (currentModule?.type === ContentType.FORM) {
            setFormData(currentModule?.formData ?? '');
          } else {
            setData(currentModule?.data ?? '');
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUnitType, defaultHomeEdContentGroups, defaultSchoolContentGroups, groups, heaGroups, isGroupsLoading, schoolGroups]);

  const getModuleContentsByGroup = useCallback(async (contentId: string) => {
    const [contentsForSchool, contentsForHomeEd]  = await Promise.all([
      isParentPreparationModule ? null : getContentsByGroup({ variables: { input: { id: contentId } } }),
      isTeacherPreparationModule ? null : getAllHeaGroupModules({ variables: { input: { id: contentId } } })
    ]);
    const groupsDataForSchool = contentsForSchool?.data?.getContentAllGroups?.groups || [];
    const groupsDataForHomeEd = contentsForHomeEd?.data?.heaGetModuleAllGroups || [];
    if (!isParentPreparationModule && groupsDataForSchool?.length) {
      const newGroups: ExtendedContent[] = groupsDataForSchool
        .reduce((previous, current) =>
          current?.groupName !== DefaultModuleEnum.Default ? [...previous, ({...content, ...current}) as ExtendedContent] : previous
        , defaultSchoolContentGroups) || [];
      setSchoolGroups(newGroups);
      setGroups(newGroups);
    } else {
      setGroups(defaultSchoolContentGroups);
    }

    if (!isTeacherPreparationModule && groupsDataForHomeEd?.length) {
      const newGroups: ExtendedContent[] = groupsDataForHomeEd
        .reduce((previous, current) =>
          current?.groupName !== DefaultModuleEnum.HomeEd ? [...previous, ({...content, ...current}) as ExtendedContent] : previous
        , defaultHomeEdContentGroups) || [];
      setHeaGroups(newGroups);
    }
  }, [isParentPreparationModule, getContentsByGroup, isTeacherPreparationModule, getAllHeaGroupModules, defaultSchoolContentGroups, content, defaultHomeEdContentGroups]);

  useEffect(() => {
    if (content?.id) {
      if (content.type !== ContentType.PARENT) {
        getModuleContentsByGroup(content?.id);
      }
      clearFile();
      setName(content?.name);
      setDescription(content?.description || '');
      setFileUploadingPercent(0);
      setFileUploadFinished(false);
      setPauseHere(content?.pauseHere ?? false);
      setIsRequired(content?.isRequired ?? false);
      setIsLastLesson(content?.lastInLesson ?? false);
      setIsAdditionalAfter(content.additionalAfter ?? false)
      if (content?.type === ContentType.FORM) {
        setFormData(content?.formData ?? '');
        setData(content?.data ?? '');
      } else {
        setData(content?.data ?? '');
      }
      if (homeEdModule) {
        setHomeEdState(!!homeEdModule?.useDefault || homeEdModule?.useDefault === undefined  ? DefaultModuleEnum.Default : DefaultModuleEnum.HomeEd);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearFile, content?.id]);

  useEffect(() => {
    clearFile();
    if (content?.id) {
      setUnitType(UnitTypes.School);
      setHeaGroups(defaultHomeEdContentGroups);
      setSchoolGroups(defaultSchoolContentGroups);
      setActiveTab(DefaultModuleEnum.Default);
      setGroups(defaultSchoolContentGroups);
    }
  }, [clearFile, content?.id, defaultHomeEdContentGroups, defaultSchoolContentGroups]);

  const updateVideoContentFunc = useCallback(async (input: UpdateVideoContentInput, group?: string) => {
    if (group) {
      setLoading(true);
      try {
        let result;
        while (!result?.data?.updateVideoGroupContent?.data) {
          result = await updateVideoGroupContent({ variables: { input: {
            id: input.id,
            uploadId: input.uploadId,
            data: input.data,
            group,
          } } });
        }
      } catch (e) {
        logger.info('e---', e);
        await updateVideoGroupContent({ variables: { input: {
          id: input.id,
          uploadId: input.uploadId,
          data: input.data,
          group,
        } } });
      }
      setLoading(false);
    } else {
      try {
        await updateVideoContent({ variables: { input }});
      } catch (e) {
        logger.info('e1---', e);
        await updateVideoContent({ variables: { input }});
      }
    }
    setLoading(false);
    setFileUploadingPercent(0);
    setFileUploadFinished(false);
  }, [updateVideoContent, updateVideoGroupContent]);

  const onProgress = useCallback((percent: number) => {
    setFileUploadingPercent(percent)
  }, [setFileUploadingPercent]);

  const uploadVideoAndSave = useCallback(async (dataFile: File, data: { url: string; uploadId: string }) => {
    if (currentContent!.type === ContentType.VIDEO) {
      setLoading(true);
      if (dataFile && data.url && data.uploadId) {
        await handleUploadVideo(dataFile, data.url, onProgress)
        setFileLoading(false);
        setFileUploadFinished(true);
        await sleep(1000);
        await updateVideoContentFunc({
          id: currentContent!.id,
          type: currentContent!.type,
          name,
          description,
          position: groups[0]?.position ?? 1,
          pauseHere: pauseHere,
          lastInLesson: isLastLesson,
          uploadId: data.uploadId,
          data: data.url,
        }, isGroupContent ? currentContent?.groupId : undefined);
      }
      setLoading(false);
    }
  }, [currentContent, onProgress, updateVideoContentFunc, name, description, groups, pauseHere, isLastLesson, isGroupContent]);

  const handleSave = useCallback(async () => {
    if (customLoading || fileLoading || !currentContent) return;
    const errors: Partial<Record<ErrorKeys, string>> = {};

    let updatedFormData = formData;
    if (currentContent?.type === ContentType.FORM) {
      if (hasFeedbackFormErrors) {
        errors.feedbackForm = 'Feed feedback form'
        setErrors(errors);
        errorToast('Cannot save lesson. Please check that all Required answers are selected.');
        return;
      }
      updatedFormData = JSON.stringify(getFormState());
    }
    setLoading(true);
    if (isHeaModuleTab) {
      if (isGroupContent) {
        if (currentContent?.id && currentContent?.groupId && (data || updatedFormData)) {
          await updateHeaGroupModule({
            variables: {
              input: {
                moduleId: currentContent?.contentId || currentContent?.id,
                groupId: currentContent.groupId,
                data: data || '',
                formData: updatedFormData,
              }
            }
          });
        }
      } else if (heaUnit?.id && currentContent?.id) {
        await updateHeaModule({
          variables: {
            input: {
              id: currentContent.id,
              type: currentContent.type,
              name,
              formData: updatedFormData,
              unitId: heaUnit?.id,
              lessonId: heaLesson?.id,
              useDefault: homeEdState === DefaultModuleEnum.Default,
              data,
            }
          }
        });
        if (homeEdState !== DefaultModuleEnum.Default) {
          markHeaModuleSaved(currentContent.id);
        }
      }
      setLoading(false);
      return;
    }

    if (isGroupContent) {
      if (currentContent?.id && currentContent?.groupId) {
        await updateGroupContent({
          variables: {
            input: {
              id: currentContent?.contentId || currentContent?.id,
              group: currentContent?.groupId,
              data: data || '',
              formData: updatedFormData,
            }
          }
        })
      }
    } else {
      await updateContent({
        variables: {
          input: {
            id: currentContent!.id,
            type: currentContent!.type,
            name,
            description,
            pauseHere: pauseHere,
            isRequired: isRequired,
            lastInLesson: isLastLesson,
            additionalAfter: isAdditionalAfter,
            data: data || '',
            formData: updatedFormData,
          }
        }
      });
    }
    setLoading(false);
  }, [customLoading, fileLoading, currentContent, formData, isHeaModuleTab, heaUnit, isGroupContent, hasFeedbackFormErrors, getFormState, data, updateHeaGroupModule, updateHeaModule, name, heaLesson?.id, homeEdState, updateGroupContent, updateContent, description, pauseHere, isLastLesson, isAdditionalAfter, isRequired, errorToast]);

  const handleContentDataSave = useCallback(async (data: string) => {
    if (!currentContent) return;
    setLoading(true);
    if (isGroupContent) {
      if (isHeaModuleTab && heaUnit) {
        await updateHeaGroupModule({
          variables: {
            input: {
              moduleId: currentContent?.contentId || currentContent?.id,
              groupId: currentContent.groupId,
              data,
            }
          }
        });
      } else {
        await updateGroupContent({
          variables: {
            input: {
              id: currentContent?.contentId || currentContent?.id,
              group: currentContent?.groupId,
              data,
            }
          }
        });
      }
    } else {
      if (isHeaModuleTab && heaUnit) {
        await updateHeaModule({
          variables: {
            input: {
              id: currentContent.id,
              type: currentContent.type,
              name,
              data,
              unitId: heaUnit.id,
              lessonId: heaLesson?.id,
              useDefault: homeEdState === DefaultModuleEnum.Default,
            }
          }
        })
        if (homeEdState !== DefaultModuleEnum.Default) {
          markHeaModuleSaved(currentContent.id);
        }
      } else {
        await updateContent({
          variables: {
            input: {
              id: currentContent!.id,
              type: currentContent!.type,
              name,
              description,
              data,
              pauseHere: pauseHere,
              isRequired: isRequired,
              lastInLesson: isLastLesson,
              additionalAfter: isAdditionalAfter
            }
          }
        });
      }
    }
    setLoading(false);
  }, [currentContent, description, heaLesson?.id, heaUnit, homeEdState, isGroupContent, isHeaModuleTab, isLastLesson, isAdditionalAfter, name, pauseHere, isRequired, updateContent, updateGroupContent, updateHeaGroupModule, updateHeaModule]);

  const handleRemoveModule = useCallback(async () => {
    if (window.confirm('Are you sure you want to remove the Module?')) {
      setLoading(true);
      if (currentContent?.id) {
        if (isHeaModuleTab) {
          if (homeEdModule?.id) {
            await deleteHeaModule({
              variables: {
                input: { id: homeEdModule?.id }
              }
            })
          }
        } else {
          await removeContent({
            variables: {
              input: { id: currentContent!.id },
            }
          });
          if (homeEdModule?.id) {
            await deleteHeaModule({
              variables: {
                input: { id: homeEdModule?.id }
              }
            })
          }
        }

        const existingGroups: ExtendedContent[] = groups.slice(1);
        if (existingGroups.length > 0) {
          await Promise.all(existingGroups.map(group => {
            if (isHeaModuleTab) {
              if (homeEdModule?.id) {
                return deleteHeaGroupModule({
                  variables: {
                    input: {
                      moduleId: homeEdModule?.id,
                      groupId: group?.groupId
                    }
                  }
                });
              }
              return null;
            } else {
              return Promise.all([
                homeEdModule?.id ? deleteHeaGroupModule({
                  variables: {
                    input: {
                      moduleId: homeEdModule?.id,
                      groupId: group?.groupId
                    }
                  }
                }) : null,
                deleteGroupContent({
                  variables: {
                    input: {
                      id: currentContent.id,
                      group: group?.groupId
                    }
                  }
                })
              ])
            }
          }
          ));
        }
        if (isHeaModuleTab) {
          setHeaGroups([]);
          setGroups(schoolGroups?.length ? [...schoolGroups] : [...defaultSchoolContentGroups]);
          setActiveTab(DefaultModuleEnum.Default);
        }
      }
      setLoading(false);
    }
  }, [currentContent, isHeaModuleTab, groups, homeEdModule?.id, deleteHeaModule, removeContent, deleteHeaGroupModule, deleteGroupContent, schoolGroups, defaultSchoolContentGroups]);

  const onChangeHomeEdUseDefaultState = useCallback((val: string) => {
    setHomeEdState(val as DefaultModuleEnum)
  }, []);

  const onChangeRequiredLesson = useCallback((value: boolean) => {
    setIsRequired(value);
  }, []);

  const onChangeLastLessonMark = useCallback((value: boolean) => {
    setIsLastLesson(value);
  }, []);

  const onChangeAdditionalAfter = useCallback((value: boolean) => {
    setIsAdditionalAfter(value);
  }, []);

  const loading = useMemo(() => customLoading || schoolModuleLoading || heaModuleLoading || heaGroupLoading, [customLoading, heaGroupLoading, heaModuleLoading, schoolModuleLoading]);
  const isDisabled = useMemo(() =>  loading || urlLoading || fileLoading || isRemovingModule || isHeaModuleRemoving
    , [fileLoading, isHeaModuleRemoving, isRemovingModule, loading, urlLoading]);

  const handleChangeVideo = useCallback(async (video: Video | null) => {
    if (currentContent!.type === ContentType.VIDEO) {
      setLoading(true);
      setFileLoading(false);
      setFileUploadFinished(true);
      if (video) {
        await updateVideoContentFunc({
          id: currentContent!.id,
          type: currentContent!.type,
          name,
          position: groups[0]?.position ?? 1,
          pauseHere: pauseHere,
          lastInLesson: isLastLesson,
          uploadId: '',
          data: video.data,
          meta: video.meta
        }, isGroupContent ? currentContent?.groupId : undefined);
      }
      setLoading(false);
    }
  }, [currentContent, groups, isGroupContent, isLastLesson, name, pauseHere, updateVideoContentFunc])

  return {
    loading,
    isGroupsLoading: isGroupsLoading || isHeaGroupsLoading || deletingModuleData,
    isRemoving: isRemovingModule || isHeaModuleRemoving,
    isGroupRemoving: isSchoolGroupRemoving || isHomeEdGroupRemoving || deletingModuleData || isRemovingModule,
    urlLoading,
    availableGroupOptions,
    getUrl,
    clearFile,
    handleSave,
    handleRemoveModule,
    handleRemoveGroup: currentUnitType === UnitTypes.HomeEd ? handleHomeEdRemoveGroup : handleRemoveGroup,
    handleAddGroup,
    onChangeTab,
    onAddGroup,
    errors,
    formData,
    fileUploadingPercent,
    setFileUploadingPercent,
    fileUploadFinished,
    activeTab,
    isOpenAddModal,
    setErrors,
    fileLoading,
    groups,
    name,
    description,
    setName,
    setDescription,
    onCloseAddGroupModal,
    currentContent,
    data,
    setData,
    dataInputRef,
    uploadVideoAndSave,
    isDisabled,
    handleContentDataSave,
    deleteGroupModuleData,
    deleteModuleData,
    isRequired,
    onChangeRequiredLesson,
    onChangeLastLessonMark,
    isLastLesson,
    onChangeAdditionalAfter,
    isAdditionalAfter,
    isClassPresentation: !!lesson?.id,
    isParentPreparationModule,
    isTeacherPreparationModule,
    isGroupContent,
    handleUnitTypeChange,
    currentUnitType,
    onChangeHomeEdUseDefaultState,
    homeEdState,
    isHomeEdModuleSaved,
    isPreparation,
    handleChangeVideo
  }
}
